import { PolicyLayout } from '../components/PolicyLayout';
import { ContactBlock } from '../components/ContactBlock';

const SECTIONS = [
  { id: 'acceptance', title: 'Acceptance of Terms' },
  { id: 'definitions', title: 'Definitions' },
  { id: 'eligibility', title: 'Eligibility' },
  { id: 'account-security', title: 'Account Registration and Security' },
  { id: 'service-description', title: 'Service Description' },
  { id: 'service-user-obligations', title: 'Service User Obligations' },
  { id: 'provider-obligations', title: 'Care Provider Obligations' },
  { id: 'bookings', title: 'Booking Terms' },
  { id: 'reviews', title: 'Reviews and Ratings' },
  { id: 'provider-verification', title: 'Provider Verification' },
  { id: 'provider-blocking', title: 'Provider Blocking and Suspension' },
  { id: 'communications', title: 'Communications' },
  { id: 'posts', title: 'Care Need Posts' },
  { id: 'pricing', title: 'Pricing and Payments' },
  { id: 'account-deletion', title: 'Account Deletion' },
  { id: 'intellectual-property', title: 'Intellectual Property' },
  { id: 'limitation-of-liability', title: 'Limitation of Liability' },
  { id: 'disclaimers', title: 'Disclaimers' },
  { id: 'indemnification', title: 'Indemnification' },
  { id: 'governing-law', title: 'Governing Law' },
  { id: 'dispute-resolution', title: 'Dispute Resolution' },
  { id: 'modifications', title: 'Modifications to Terms' },
  { id: 'general-provisions', title: 'General Provisions' },
  { id: 'contact', title: 'Contact Information' },
];

export const TermsOfService = () => {
  return (
    <PolicyLayout title="Terms of Service" lastUpdated="22 April 2026" sections={SECTIONS}>
      <section id="acceptance">
        <h2>Acceptance of Terms</h2>
        <ul>
          <li>
            By accessing or using NursingCareBooking.com ("the Platform"), you agree to be bound by
            these Terms of Service ("Terms").
          </li>
          <li>If you do not agree to these Terms, you must not use the Platform.</li>
          <li>
            These Terms constitute a legally binding agreement between you and
            NursingCareBooking.com.
          </li>
          <li>
            By creating an account, you confirm that you have read, understood, and agree to these
            Terms and our Privacy Policy.
          </li>
        </ul>
      </section>

      <section id="definitions">
        <h2>Definitions</h2>
        <ul>
          <li>
            <strong>"Platform"</strong> means the NursingCareBooking.com website and all associated
            services.
          </li>
          <li>
            <strong>"Service User"</strong> means an individual who registers to find and book care
            services for themselves or others.
          </li>
          <li>
            <strong>"Care Provider"</strong> means an individual or organisation registered to offer
            care services through the Platform.
          </li>
          <li>
            <strong>"Booking"</strong> means a care service appointment arranged through the Platform
            between a Service User and a Care Provider.
          </li>
          <li>
            <strong>"CQC"</strong> means the Care Quality Commission, the independent regulator of
            health and social care in England.
          </li>
          <li>
            <strong>"Account"</strong> means your registered user account on the Platform.
          </li>
          <li>
            <strong>"Content"</strong> means any information, text, images, reviews, messages, or
            other materials submitted to the Platform.
          </li>
        </ul>
      </section>

      <section id="eligibility">
        <h2>Eligibility</h2>
        <ul>
          <li>You must be at least 18 years of age to create an account.</li>
          <li>You must be a resident of the United Kingdom.</li>
          <li>
            You must provide accurate, current, and complete information during registration.
          </li>
          <li>You must have the legal capacity to enter into a binding agreement.</li>
          <li>
            Care providers must hold valid registration with the relevant regulatory body (e.g.,
            CQC) where required.
          </li>
          <li>One account per individual; duplicate accounts may be terminated.</li>
        </ul>
      </section>

      <section id="account-security">
        <h2>Account Registration and Security</h2>
        <ul>
          <li>
            You are responsible for maintaining the confidentiality of your account credentials.
          </li>
          <li>
            Passwords must meet our minimum security requirements: at least 12 characters,
            including uppercase and lowercase letters, numbers, and special characters.
          </li>
          <li>
            You must notify us immediately of any unauthorised access to your account.
          </li>
          <li>You are responsible for all activities that occur under your account.</li>
          <li>
            We reserve the right to suspend or terminate accounts that violate these Terms.
          </li>
          <li>You must keep your account information up to date.</li>
        </ul>
      </section>

      <section id="service-description">
        <h2>Service Description</h2>
        <ul>
          <li>
            NursingCareBooking.com is an online marketplace that connects Service Users seeking care
            services with registered Care Providers.
          </li>
          <li>We do not employ Care Providers, nor do we provide care services directly.</li>
          <li>
            We facilitate the connection between parties but are not a party to any care service
            agreement between Service Users and Care Providers.
          </li>
        </ul>
        <p>The Platform enables:</p>
        <ul>
          <li>Searching for care providers by location and care type</li>
          <li>Booking care appointments</li>
          <li>In-app messaging</li>
          <li>Posting care needs</li>
          <li>Leaving reviews and ratings</li>
          <li>Provider profile management</li>
        </ul>
        <ul>
          <li>
            We do not guarantee the availability of any Care Provider or the quality of care
            provided.
          </li>
        </ul>
      </section>

      <section id="service-user-obligations">
        <h2>Service User Obligations</h2>
        <ul>
          <li>
            Provide accurate and up-to-date medical and care information for the care recipient.
          </li>
          <li>
            Disclose relevant health conditions, allergies, medications, and mobility requirements.
          </li>
          <li>
            Provide accurate property information (address, access details, pets, floor level, lift
            availability).
          </li>
          <li>Treat Care Providers with dignity and respect.</li>
          <li>Honour confirmed bookings or cancel within reasonable timeframes.</li>
          <li>Provide a safe and suitable environment for care delivery.</li>
          <li>Ensure emergency contact information is current and accurate.</li>
          <li>Not use the Platform for any unlawful purpose.</li>
        </ul>
      </section>

      <section id="provider-obligations">
        <h2>Care Provider Obligations</h2>
        <ul>
          <li>Maintain valid CQC registration where required by law.</li>
          <li>
            Provide accurate qualifications, credentials, and experience information.
          </li>
          <li>
            Keep DBS (Disclosure and Barring Service) checks current where applicable.
          </li>
          <li>Maintain appropriate professional insurance.</li>
          <li>Respond to booking requests in a timely manner.</li>
          <li>
            Deliver care services to the standard expected of a qualified professional.
          </li>
          <li>Keep availability and pricing information up to date.</li>
          <li>
            Comply with all applicable healthcare regulations and professional standards.
          </li>
          <li>
            Maintain confidentiality of Service User and care recipient information.
          </li>
          <li>Report safeguarding concerns in accordance with legal requirements.</li>
        </ul>
      </section>

      <section id="bookings">
        <h2>Booking Terms</h2>
        <p>
          Bookings are made through the Platform by Service Users selecting a Care Provider and
          submitting a booking request. Booking statuses:
        </p>
        <ul>
          <li>
            <strong>Pending</strong> — awaiting provider response
          </li>
          <li>
            <strong>Accepted</strong> — confirmed by provider
          </li>
          <li>
            <strong>Rejected</strong> — declined by provider
          </li>
          <li>
            <strong>Cancelled</strong> — cancelled by either party
          </li>
          <li>
            <strong>Completed</strong> — care service delivered
          </li>
        </ul>
        <ul>
          <li>
            At the time of booking, a snapshot of relevant care recipient information is captured and
            shared with the Care Provider to facilitate care delivery.
          </li>
          <li>Both parties are expected to honour confirmed bookings.</li>
          <li>
            Cancellations should be made with reasonable notice. Repeated no-shows or late
            cancellations may result in account restrictions.
          </li>
          <li>Please refer to our Cancellation Policy for full details.</li>
        </ul>
      </section>

      <section id="reviews">
        <h2>Reviews and Ratings</h2>
        <ul>
          <li>
            Service Users may leave reviews and ratings for Care Providers following a completed
            booking.
          </li>
          <li>Reviews must be honest, fair, and based on genuine experience.</li>
          <li>All reviews are subject to moderation before publication.</li>
          <li>
            Reviews may be in status: <strong>pending</strong> (awaiting moderation),{' '}
            <strong>approved</strong> (published), or <strong>rejected</strong> (not published).
          </li>
        </ul>
        <p>We reserve the right to flag, hide, or remove reviews that contain:</p>
        <ul>
          <li>False or misleading information</li>
          <li>Defamatory content</li>
          <li>Personal attacks or harassment</li>
          <li>Spam or commercial solicitation</li>
          <li>Personally identifiable information of third parties</li>
          <li>Illegal content</li>
        </ul>
        <ul>
          <li>Care Providers may not offer incentives in exchange for positive reviews.</li>
        </ul>
      </section>

      <section id="provider-verification">
        <h2>Provider Verification</h2>
        <ul>
          <li>
            We endeavour to verify Care Provider credentials, including CQC registration status.
          </li>
          <li>
            A verification badge on a provider profile indicates that we have checked certain
            credentials.
          </li>
        </ul>
        <p>Verification does <strong>NOT</strong> constitute:</p>
        <ul>
          <li>An endorsement of the Care Provider</li>
          <li>A guarantee of the quality of care</li>
          <li>A certification of all stated qualifications</li>
          <li>An ongoing monitoring of the provider's status</li>
        </ul>
        <ul>
          <li>
            Service Users should exercise their own judgement when selecting a Care Provider and may
            independently verify credentials with the relevant regulatory bodies.
          </li>
        </ul>
      </section>

      <section id="provider-blocking">
        <h2>Provider Blocking and Suspension</h2>
        <p>
          We reserve the right to block or suspend Care Provider accounts for:
        </p>
        <ul>
          <li>Safety concerns</li>
          <li>Policy violations</li>
          <li>Complaints from Service Users</li>
          <li>Failure to maintain required registrations</li>
          <li>Fraudulent or misleading behaviour</li>
          <li>
            Any conduct that may harm users or the reputation of the Platform
          </li>
        </ul>
        <ul>
          <li>Blocked providers will be hidden from search results and unable to receive new bookings.</li>
          <li>Existing bookings may be affected by a provider block.</li>
          <li>Providers may appeal a blocking decision by contacting support.</li>
        </ul>
      </section>

      <section id="communications">
        <h2>Communications</h2>
        <ul>
          <li>
            The Platform provides an in-app messaging system for communication between Service Users
            and Care Providers.
          </li>
          <li>Messages must be relevant to care services and conducted professionally.</li>
        </ul>
        <p>The following is prohibited:</p>
        <ul>
          <li>
            Sharing personal contact details to circumvent the Platform
          </li>
          <li>Harassing or threatening messages</li>
          <li>Unsolicited commercial communications</li>
          <li>Sharing of inappropriate or offensive content</li>
        </ul>
        <ul>
          <li>We may monitor communications for safety and compliance purposes.</li>
          <li>Message history is retained in accordance with our data retention policy.</li>
        </ul>
      </section>

      <section id="posts">
        <h2>Care Need Posts</h2>
        <ul>
          <li>Service Users may create posts describing their care requirements.</li>
          <li>
            Posts are visible to registered Care Providers who may respond with expressions of
            interest.
          </li>
          <li>Posts must contain accurate information about care needs.</li>
        </ul>
        <p>Posts must not contain:</p>
        <ul>
          <li>False or misleading information</li>
          <li>Discriminatory content</li>
          <li>Personal contact details intended to bypass the Platform</li>
          <li>Inappropriate or offensive material</li>
        </ul>
        <ul>
          <li>We reserve the right to remove posts that violate these Terms.</li>
        </ul>
      </section>

      <section id="pricing">
        <h2>Pricing and Payments</h2>
        <ul>
          <li>Care Providers set their own rates (hourly, daily, and/or monthly).</li>
          <li>
            Pricing information displayed on provider profiles is indicative and may vary based on
            specific care requirements.
          </li>
          <li>Payment arrangements are made directly between Service Users and Care Providers.</li>
          <li>NursingCareBooking.com does not process payments between users at this time.</li>
          <li>
            Any disputes regarding pricing or payment should be resolved directly between the
            parties, though we may assist in mediation upon request.
          </li>
        </ul>
      </section>

      <section id="account-deletion">
        <h2>Account Deletion</h2>
        <ul>
          <li>You may request deletion of your account at any time through Settings.</li>
          <li>Account deletion requires a written reason and explicit confirmation.</li>
          <li>Deletion requests are reviewed by our team before processing.</li>
        </ul>

        <h3>Upon deletion for Service Users:</h3>
        <ul>
          <li>All active bookings are cancelled</li>
          <li>Care preferences and profile data are removed</li>
          <li>Provider relationships are terminated</li>
        </ul>

        <h3>Upon deletion for Care Providers:</h3>
        <ul>
          <li>CQC verification status is removed</li>
          <li>All client relationships are terminated</li>
          <li>Business data and service history are removed</li>
        </ul>

        <ul>
          <li>
            Certain data may be retained where required by law or for legitimate purposes as
            described in our Privacy Policy.
          </li>
          <li>
            You may request account reactivation within a reasonable period by contacting support.
          </li>
        </ul>
      </section>

      <section id="intellectual-property">
        <h2>Intellectual Property</h2>
        <ul>
          <li>
            All content, design, logos, and software comprising the Platform are owned by
            NursingCareBooking.com or its licensors.
          </li>
          <li>
            You retain ownership of content you submit (reviews, messages, profile information) but
            grant us a non-exclusive, worldwide, royalty-free licence to use, display, and distribute
            such content in connection with operating the Platform.
          </li>
        </ul>
        <p>You must not:</p>
        <ul>
          <li>
            Copy, modify, or distribute Platform content without permission
          </li>
          <li>Use our branding or trademarks without written consent</li>
          <li>
            Reverse-engineer or attempt to extract source code from the Platform
          </li>
          <li>Create derivative works based on the Platform</li>
        </ul>
      </section>

      <section id="limitation-of-liability">
        <h2>Limitation of Liability</h2>
        <p>
          NursingCareBooking.com acts as a marketplace connecting Service Users with Care Providers
          and is not liable for the quality, safety, or suitability of care services provided. To
          the maximum extent permitted by law, we shall not be liable for:
        </p>
        <ul>
          <li>
            Any loss or damage arising from the use or inability to use the Platform
          </li>
          <li>
            The conduct, actions, or omissions of any Care Provider or Service User
          </li>
          <li>Any indirect, incidental, special, or consequential damages</li>
          <li>Loss of data, profits, or business opportunities</li>
        </ul>
        <ul>
          <li>
            Our total liability to you for any claims arising from or relating to these Terms or the
            Platform shall not exceed the greater of the fees paid by you to us in the 12 months
            preceding the claim, or one hundred pounds (GBP 100).
          </li>
          <li>
            Nothing in these Terms excludes or limits our liability for death or personal injury
            caused by our negligence, fraud or fraudulent misrepresentation, or any liability that
            cannot be excluded by law.
          </li>
        </ul>
      </section>

      <section id="disclaimers">
        <h2>Disclaimers</h2>
        <p>
          The Platform is provided on an "as is" and "as available" basis. We do not warrant that:
        </p>
        <ul>
          <li>
            The Platform will be available at all times without interruption
          </li>
          <li>Any Care Provider will be available for booking</li>
          <li>
            The information provided by Care Providers is accurate or complete
          </li>
          <li>The Platform will meet your specific requirements</li>
          <li>
            The Platform is free from errors, viruses, or other harmful components
          </li>
        </ul>
        <ul>
          <li>
            We do not provide medical advice. Information on the Platform is for care matching
            purposes only and should not be relied upon as a substitute for professional medical
            advice.
          </li>
          <li>
            We do not guarantee any specific outcomes from care services arranged through the
            Platform.
          </li>
        </ul>
      </section>

      <section id="indemnification">
        <h2>Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless NursingCareBooking.com, its officers, directors,
          employees, and agents from any claims, damages, losses, liabilities, costs, and expenses
          (including legal fees) arising from:
        </p>
        <ul>
          <li>Your use of the Platform</li>
          <li>Your violation of these Terms</li>
          <li>Your violation of any third-party rights</li>
          <li>Any content you submit to the Platform</li>
          <li>
            Any care services you provide or receive through the Platform
          </li>
        </ul>
      </section>

      <section id="governing-law">
        <h2>Governing Law</h2>
        <ul>
          <li>
            These Terms are governed by and construed in accordance with the laws of England and
            Wales.
          </li>
          <li>
            Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of
            the courts of England and Wales.
          </li>
          <li>
            If you are a consumer, you may also benefit from mandatory consumer protection provisions
            of the laws of your country of residence.
          </li>
        </ul>
      </section>

      <section id="dispute-resolution">
        <h2>Dispute Resolution</h2>
        <ul>
          <li>We encourage parties to resolve disputes amicably through direct communication.</li>
          <li>
            If you have a complaint, please contact our support team at{' '}
            <a href="mailto:Info@nursingcarebooking.com">Info@nursingcarebooking.com</a>.
          </li>
          <li>
            We will endeavour to acknowledge complaints within 2 working days and provide a
            resolution within 14 working days.
          </li>
        </ul>
        <p>If we are unable to resolve your complaint satisfactorily, you may escalate to:</p>
        <ul>
          <li>The relevant healthcare ombudsman</li>
          <li>
            The Care Quality Commission (for care quality concerns)
          </li>
          <li>
            The Information Commissioner's Office (for data protection concerns)
          </li>
          <li>Alternative dispute resolution services</li>
        </ul>
      </section>

      <section id="modifications">
        <h2>Modifications to Terms</h2>
        <ul>
          <li>We reserve the right to modify these Terms at any time.</li>
          <li>
            Material changes will be communicated via email or a prominent notice on the Platform at
            least 14 days before they take effect.
          </li>
          <li>
            Your continued use of the Platform following changes constitutes acceptance of the
            modified Terms.
          </li>
          <li>
            If you disagree with any changes, you may delete your account before the changes take
            effect.
          </li>
        </ul>
      </section>

      <section id="general-provisions">
        <h2>General Provisions</h2>
        <ul>
          <li>
            <strong>Severability:</strong> If any provision is found to be unenforceable, the
            remaining provisions shall remain in full force.
          </li>
          <li>
            <strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy, Cookie
            Policy, and other referenced policies, constitute the entire agreement between you and
            NursingCareBooking.com.
          </li>
          <li>
            <strong>Waiver:</strong> Our failure to enforce any provision shall not be deemed a
            waiver of that provision.
          </li>
          <li>
            <strong>Assignment:</strong> You may not assign your rights under these Terms. We may
            assign our rights and obligations without restriction.
          </li>
          <li>
            <strong>Force Majeure:</strong> We shall not be liable for any failure to perform due to
            circumstances beyond our reasonable control.
          </li>
        </ul>
      </section>

      <section id="contact">
        <h2>Contact Information</h2>
        <ContactBlock
          introText="For questions or concerns about these Terms:"
          extraContent={
            <p>
              Website:{' '}
              <a href="https://nursingcarebooking.com" target="_blank" rel="noopener noreferrer">
                nursingcarebooking.com
              </a>
            </p>
          }
        />
      </section>
    </PolicyLayout>
  );
};
